import React, { useState } from 'react';
import styled from 'styled-components';
import { useFreeListens } from '../../contexts/FreeListenContext';
import { useUser } from '../../contexts/UserContext';

const BannerContainer = styled.div<{ $isVisible: boolean }>`
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px 20px;
  display: ${props => props.$isVisible ? 'flex' : 'none'};
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 100;
  border-radius: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
`;

const Message = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const MessageText = styled.span`
  font-size: 15px;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const Counter = styled.span`
  background-color: rgba(255, 255, 255, 0.25);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const SignUpButton = styled.button`
  background-color: white;
  color: #667eea;
  border: none;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: #f0f0f0;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    flex: 1;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

interface FreeListenBannerProps {
  onSignUpClick: () => void;
}

export function FreeListenBanner({ onSignUpClick }: FreeListenBannerProps) {
  const { hasUsedFreeListen, getRemainingListens } = useFreeListens();
  const { isLoggedIn } = useUser();
  const [isDismissed, setIsDismissed] = useState(false);

  const remaining = getRemainingListens();

  // Don't show banner if:
  // - User is logged in
  // - User hasn't used any free listens yet
  // - Banner was dismissed
  const shouldShow = !isLoggedIn && hasUsedFreeListen && !isDismissed;

  const getMessageText = () => {
    if (remaining === 1) {
      return "You have 1 listen remaining without logging in";
    } else if (remaining === 0) {
      return "Sign up to continue listening for free";
    }
    return `You have ${remaining} listens remaining without logging in`;
  };

  return (
    <BannerContainer $isVisible={shouldShow}>
      <Message>
        <MessageText>{getMessageText()}</MessageText>
        {remaining > 0 && (
          <Counter>{remaining} left</Counter>
        )}
      </Message>

      <Actions>
        <SignUpButton onClick={onSignUpClick}>
          Sign Up for Unlimited Access
        </SignUpButton>
        <CloseButton onClick={() => setIsDismissed(true)} aria-label="Dismiss banner">
          ×
        </CloseButton>
      </Actions>
    </BannerContainer>
  );
}
